import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-native';
import { GameUtils } from './GameUtils';
import { Button, Microphone, Pressable, Text, View } from '../../components';
import { useAppState, useSystemState } from '../../context';
import { Colors } from '../../styles';
import { Console, Optional, Validate } from '../../utils';
import { languageColors } from '../../constants';

const NAME = 'CrossWordPuzzle';

const DARK = 'dark';
const LIGHT = 'light';

const DUMMY_COORD = -1;
const EMPTY_STRING = '';
const EMPTY_ARRAY = [];


export const CrossWordPuzzle = props => {

    const { deviceScale, os } = useSystemState();
    const { DEFAULT, dark, language, t } = useAppState();
    const { KB_BUTTON_HEIGHT, KB_BUTTON_WIDTH, KB_BUTTON_RADIUS, KB_MARGIN_HZ, KB_MARGIN_VT, KB_ROW_HEIGHT } = DEFAULT;

    const [coordinate, setCoordinate] = useState({ i: DUMMY_COORD, j: DUMMY_COORD });
    const [puzzle, setPuzzle] = useState({ matrix: EMPTY_ARRAY, words: EMPTY_ARRAY });
    const [show, setShow] = useState(false);
    const [target, setTarget] = useState(EMPTY_STRING);
    const [value, setValue] = useState(EMPTY_STRING);
    const [transcription, setTranscription] = useState(EMPTY_STRING);
    const [unlocked, setUnlocked] = useState(EMPTY_ARRAY);

    const setCoordinateRef = useRef(setCoordinate);
    const setTargetRef = useRef(setTarget);
    const setTranscriptionRef = useRef(setTranscription);
    const setUnlockedRef = useRef(setUnlocked);
    const setPuzzleRef = useRef(setPuzzle);
    const setShowRef = useRef(setShow);
    const setValueRef = useRef(setValue);

    useEffect(
        () => {
            Console.log(`${NAME} useEffect createNewPuzzle`, { language });
            createNewPuzzle();
        },
        [
            language, // force a new puzzle if the language changes
            createNewPuzzle,
        ],
    );

    useEffect(
        () => {
            Console.log(`${NAME} useEffect match`, { target, transcription });
            if (Validate.isValidNonEmptyString(transcription) &&
                Validate.isValidNonEmptyString(target)) {
                if (GameUtils.isMatch(os, language, transcription, target)) {
                    var coords = [];
                    puzzle.matrix.forEach((row, i) => {
                        row.forEach((cell, j) => {
                            if (cell.isUsed && !GameUtils.isUnlocked(i, j, unlocked) && cell.words.includes(target)) {
                                coords.push({ i, j });
                            }
                        });
                    });
                    setUnlockedRef.current([...unlocked, ...coords]);
                } else {
                    Console.log(`${NAME} useEffect NO MATCH`, { transcription, target });
                    Alert.alert(null, transcription, [{ text: 'OK' }]);
                }
                setTranscriptionRef.current(EMPTY_STRING);
                onSelect();
            }
        },
        [
            language,
            puzzle.matrix,
            os,
            transcription,
            target,
            unlocked,
            setTranscriptionRef,
            setUnlockedRef,
            onSelect,
        ],
    );

    const createNewPuzzle = useCallback(
        () => {
            Console.log(`${NAME}.createNewPuzzle`);
            const { category, game } = GameUtils.newPuzzle(t);
            setPuzzleRef.current(game);
            setValueRef.current(category);
            setShowRef.current(false);
            setUnlockedRef.current(EMPTY_ARRAY);
            onSelect();
        },
        [
            setPuzzleRef,
            setShowRef,
            setValueRef,
            setUnlockedRef,
            onSelect,
            t,
        ],
    );

    const onSelect = useCallback(
        (i = DUMMY_COORD, j = DUMMY_COORD) => {
            const val = i !== DUMMY_COORD && j !== DUMMY_COORD
                ? puzzle.matrix[i][j].words[0]
                : EMPTY_STRING;
            Console.log(`${NAME}.onSelect`, { i, j, coordinate, val });
            setCoordinateRef.current({ i, j });
            setTargetRef.current(val);
        },
        [
            coordinate,
            puzzle.matrix,
            setCoordinateRef,
            setTargetRef,
        ],
    );

    const onPress = useCallback(
        (i, j, cell) => {
            Console.log(`*** ${cell.value} ***`, { i, j, cell });
            if (!GameUtils.isUnlocked(i, j, unlocked) && /[a-z]/.test(cell.value)) {
                //      setShow(v => !v);
                if (cell.icon === '?') {
                    setUnlockedRef.current([...unlocked, { i, j }]);
                    onSelect();
                } else {
                    onSelect(i, j);
                }
            } else {
                onSelect();
            }
        },
        [
            setUnlockedRef,
            onSelect,
            unlocked,
        ],
    );

    const Cell = useCallback(
        (i, j, cell) => {
            const { icon, isUsed, words } = cell;
            const title = cell.value;
            const showValue = GameUtils.isUnlocked(i, j, unlocked);

            const fontSize = KB_BUTTON_HEIGHT * 0.5 * deviceScale;
            const keyboardButtonText = {
                fontSize,
                lineHeight: fontSize * 1.15,
                fontWeight: show ? 'bold' : 'normal',
            };
            const keyboardButton = {
                width: KB_BUTTON_WIDTH * deviceScale,
                height: KB_BUTTON_HEIGHT * deviceScale,
                borderRadius: KB_BUTTON_RADIUS * deviceScale,
                marginVertical: KB_MARGIN_VT * deviceScale,
                marginHorizontal: KB_MARGIN_HZ * deviceScale,
                alignItems: 'center',
                justifyContent: 'center',
            };

            if (isUsed) {
                if (coordinate.i === i && coordinate.j === j) {
                    Console.log(`${NAME}.Cell`, { i, j, c: title, icon, words, show: showValue });
                }
            }
            //                    onEnd={onSelect}

            return Optional(coordinate.i === i && coordinate.j === j,
                (
                    <Microphone
                        key={'microphone'}
                        style={keyboardButton}
                        setTranscriptionRef={setTranscriptionRef}
                    />
                ),
                (
                    <Pressable
                        key={`game_${i}_${j}`}
                        style={keyboardButton}
                        textStyle={keyboardButtonText}
                        backgroundColor={
                            title === EMPTY_STRING
                                ? languageColors[language][dark ? DARK : LIGHT].backgroundColor
                                : show
                                    ? languageColors[language][dark ? LIGHT : DARK].backgroundColor
                                    : Colors.colors.white
                        }
                        textColor={
                            title === EMPTY_STRING
                                ? languageColors[language][dark ? DARK : LIGHT].color
                                : show
                                    ? languageColors[language][dark ? LIGHT : DARK].color
                                    : Colors.colors.black
                        }
                        value={show || showValue ? title : icon}
                        disabled={isUsed ? false : true}
                        onPress={() => onPress(i, j, cell)}
                    />
                ));
        },
        [
            KB_BUTTON_HEIGHT,
            KB_BUTTON_WIDTH,
            KB_BUTTON_RADIUS,
            KB_MARGIN_HZ,
            KB_MARGIN_VT,
            deviceScale,
            dark,
            coordinate,
            language,
            onPress,
            show,
            unlocked,
            setTranscriptionRef,
        ],
    );

    const Row = useCallback(
        (row, i) => {
            const keyboardRow = {
                height: KB_ROW_HEIGHT * deviceScale,
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
            };
            Console.log(`${NAME}.Row`, { i });
            return (
                <View
                key={`game_row_${i}`}
                value={`CrosswordRow_${i}`}
                    style={keyboardRow}
                >
                    {row.map((cell, j) => Cell(i, j, cell))}
                </View>
            );
        },
        [
            KB_ROW_HEIGHT,
            deviceScale,
            Cell,
        ],
    );

    Console.stack(NAME, props, { KB_BUTTON_HEIGHT, KB_BUTTON_WIDTH, KB_BUTTON_RADIUS, KB_MARGIN_HZ, KB_MARGIN_VT, KB_ROW_HEIGHT, deviceScale, value, words: puzzle.words, target });

    return useMemo(
        () => {
            const smallValue = 5 * deviceScale;
            const gameView = {
                padding: smallValue,
                alignItems: 'center',
                justifyContent: 'space-around',
            };
            const gameButtonRow = {
                padding: smallValue,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-around',
            };
            const gameButton = {
                marginHorizontal: smallValue,
            };
            Console.log(`${NAME} render`, { puzzle, value, show, transcription });
            return (
                <View
                    value={'Crossword'}
                    style={gameView}
                >
                    {puzzle.matrix.map((row, i) => Row(row, i))}
                    <View
                        value={'CrosswordButtonRow'}
                        style={gameButtonRow}
                    >
                        <Button
                            style={gameButton}
                            value={value}
                            onPress={createNewPuzzle}
                        />
                        <Button
                            style={gameButton}
                            value={show ? 'Hide' : 'Show'}
                            onPress={() => { setShowRef.current(v => !v); }}
                        />
                    </View>
                    <Text value={transcription} />
                </View>
            );
        },
        [
            transcription,
            deviceScale,
            puzzle,
            value,
            show,
            createNewPuzzle,
            Row,
            setShowRef,
        ],
    );
};
